/* eslint-disable */
// src/components/VisitTransactionDetails/selectedVisitTransactionDetails.js

import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import { Alert, Box, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import VisitTransactionService from "services/visitTransactionService";
import VisitMachineService from "services/visitMachineService";
import LocalMachineService from "services/localMachineService";
import VisitTransactionDetails from "components/VisitTransactionDetails/visitTransactionDetails";
import EditVisitTransactionButton from "components/VisitTransactionDetails/editVisitTransactionButton";
import ErrorBoundary from "components/common/ErrorBoundary";
import Skeleton from "@mui/material/Skeleton";
import { getCurrentRole } from "utils/auth";
import useNotify from "hooks/useNotify";
import { buildCollectionTicketModel, generateCollectionTicketPdf } from "utils/collectionTicket";
import {
  isInsertedInCurrentVisitNoticeMachine,
  isInsertedInCurrentVisitNoticeRow,
} from "utils/visitCollectionState";
import { secondaryOutlinedButtonSx } from "components/common/buttonStyles";
import { normalizeVisitDetailLocal } from "layouts/visitMachine/collectionWorkflow";

export default function SelectedVisitTransactionDetails() {
  const { id: localIdParam, visitId: visitIdParam } = useParams();
  const visitId = useMemo(() => visitIdParam || localIdParam || null, [visitIdParam, localIdParam]);
  const localId = useMemo(() => (visitIdParam ? localIdParam : null), [localIdParam, visitIdParam]);

  const [detailData, setDetailData] = useState(null);
  const [informativeMachines, setInformativeMachines] = useState([]);
  const [informativeMachinesLoading, setInformativeMachinesLoading] = useState(true);
  const [informativeMachinesError, setInformativeMachinesError] = useState(null);
  const [informativeMachinesContextKey, setInformativeMachinesContextKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || null;
  const currentRole = getCurrentRole();
  const canViewLocalCollections = currentRole === "admin" || currentRole === "user";
  const { notifyError } = useNotify();

  const handleBackToRecaudaciones = () => {
    if (!canViewLocalCollections) {
      if (effectiveLocalId) {
        navigate(`/local/${effectiveLocalId}`);
        return;
      }
      navigate("/local");
      return;
    }

    if (returnTo?.pathname) {
      navigate(
        {
          pathname: returnTo.pathname,
          search: returnTo.search || "",
          hash: returnTo.hash || "",
        },
        {
          state: {
            ...(returnTo.state ?? {}),
            viewState: returnTo.viewState ?? null,
          },
        }
      );
      return;
    }

    if (effectiveLocalId) {
      navigate(`/local/${effectiveLocalId}/recaudaciones`);
      return;
    }

    navigate("/allVisitTransactions");
  };

  const dateFmt = new Intl.DateTimeFormat("es-UY", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    if (!visitId) {
      setError("Falta visitId");
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const data = await VisitTransactionService.getVisitTransactionDetails(
          String(visitId),
          { localId: localId ? String(localId) : null },
          { signal: ctrl.signal }
        );

        if (!alive) return;
        setDetailData(data || null);
        setError(null);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error(e);
        if (!alive) return;
        if (e.name !== "AbortError") setError("No se pudo cargar el detalle");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      try {
        ctrl.abort();
      } catch {}
    };
  }, [visitId, localId]);

  const effectiveLocalId = useMemo(() => {
    if (localId) return Number(localId);
    const fromVisit = detailData?.visit?.LocalId;
    const fromVWC = detailData?.visitWithCollection?.LocalId;
    return fromVisit ?? fromVWC ?? null;
  }, [localId, detailData?.visit?.LocalId, detailData?.visitWithCollection?.LocalId]);

  const displayDate = useMemo(() => {
    const d = detailData?.visit?.Date ? new Date(detailData.visit.Date) : null;
    return d ? dateFmt.format(d) : "";
  }, [detailData?.visit?.Date, dateFmt]);

  const isLatestVisit = Boolean(detailData?.isLatestEditable);
  const canonicalDetailLocal = useMemo(
    () =>
      normalizeVisitDetailLocal({
        local: detailData?.local || null,
        person: detailData?.person,
      }),
    [detailData?.local, detailData?.person]
  );
  const mergedMachines = useMemo(() => {
    const persisted = Array.isArray(detailData?.machines) ? detailData.machines : [];
    if (!informativeMachines.length) return persisted;
    return [...persisted, ...informativeMachines];
  }, [detailData?.machines, informativeMachines]);

  const detailDataForTicket = useMemo(() => {
    if (!detailData) return null;
    return {
      ...detailData,
      machines: mergedMachines,
    };
  }, [detailData, mergedMachines]);

  const handlePrintTicket = async () => {
    if (!detailDataForTicket) return;
    try {
      const ticketModel = buildCollectionTicketModel(detailDataForTicket);
      generateCollectionTicketPdf(ticketModel);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error(e);
      notifyError("No se pudo generar el ticket PDF.");
    }
  };

  useEffect(() => {
    let alive = true;

    const effectiveVisitDateTime = detailData?.visit?.DateTime || detailData?.visit?.Date || null;
    const contextKey =
      effectiveLocalId && effectiveVisitDateTime
        ? `${Number(effectiveLocalId)}|${effectiveVisitDateTime}`
        : null;
    if (!effectiveLocalId || !effectiveVisitDateTime) {
      setInformativeMachines([]);
      setInformativeMachinesLoading(false);
      setInformativeMachinesError(null);
      setInformativeMachinesContextKey(null);
      return () => {
        alive = false;
      };
    }

    setInformativeMachinesLoading(true);
    setInformativeMachinesError(null);
    setInformativeMachinesContextKey(null);
    (async () => {
      try {
        const [previousRows, localMachines] = await Promise.all([
          VisitMachineService.getCollectionPreviousValuesByLocal(
            Number(effectiveLocalId),
            effectiveVisitDateTime
          ),
          LocalMachineService.getLocalMachineByLocal(Number(effectiveLocalId)),
        ]);
        if (!alive) return;

        const persistedIds = new Set(
          (detailData?.machines || []).map((row) => Number(row?.LocalMachineId))
        );
        const localMachineById = new Map(
          (localMachines || []).map((row) => [Number(row?.LocalMachineId ?? row?.Id), row])
        );

        const derivedRows = (previousRows || [])
          .filter(
            (row) =>
              isInsertedInCurrentVisitNoticeMachine(row) &&
              !persistedIds.has(Number(row?.LocalMachineId))
          )
          .map((row) => {
            const localMachine = localMachineById.get(Number(row?.LocalMachineId)) || {};
            return {
              ...localMachine,
              ...row,
              LocalMachineId: Number(
                row?.LocalMachineId ?? localMachine?.LocalMachineId ?? localMachine?.Id
              ),
              MachineType: localMachine?.MachineType || {
                Type: row?.MachineType,
                Id: localMachine?.MachineTypeId ?? null,
              },
              MachineTypeId:
                row?.MachineTypeId ??
                localMachine?.MachineTypeId ??
                localMachine?.MachineType?.Id ??
                null,
              Machine: localMachine?.Machine || row?.Machine || null,
              MachineName:
                localMachine?.Machine?.Name ??
                localMachine?.MachineName ??
                row?.MachineName ??
                null,
              SerialNumber:
                localMachine?.Machine?.SerialNumber ??
                localMachine?.SerialNumber ??
                row?.SerialNumber ??
                null,
              LocalDisplayNumber:
                row?.LocalDisplayNumber ??
                localMachine?.LocalDisplayNumber ??
                localMachine?.Machine?.LocalDisplayNumber ??
                null,
              Formula: localMachine?.Formula || null,
              FormulaId: localMachine?.FormulaId ?? null,
              FormulaType: localMachine?.FormulaType ?? localMachine?.Formula?.Type ?? null,
              FormulaName: localMachine?.FormulaName ?? localMachine?.Formula?.Name ?? null,
              FormulaValue: localMachine?.FormulaValue ?? localMachine?.Formula?.Value ?? null,
              IsAccumulative:
                typeof localMachine?.IsAccumulative === "boolean"
                  ? localMachine.IsAccumulative
                  : null,
              IsInsertedInCurrentVisitNoticeRow: true,
              WasCollected: null,
              Note: null,
              note: "",
            };
          });

        setInformativeMachines(derivedRows.filter((row) => isInsertedInCurrentVisitNoticeRow(row)));
        setInformativeMachinesError(null);
        setInformativeMachinesContextKey(contextKey);
      } catch (e) {
        if (!alive) return;
        if (process.env.NODE_ENV !== "production") console.error(e);
        setInformativeMachines([]);
        setInformativeMachinesError(e);
        setInformativeMachinesContextKey(null);
      } finally {
        if (alive) setInformativeMachinesLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    detailData?.machines,
    detailData?.visit?.Date,
    detailData?.visit?.DateTime,
    effectiveLocalId,
  ]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={3} pb={3}>
          <Card>
            <MDBox mx={2} mt={-3} py={3} px={2}>
              <Skeleton variant="text" height={28} width={260} />
              <Skeleton variant="text" height={20} width={180} />
            </MDBox>
            <MDBox mx={2} mt={1} px={2} pb={3}>
              <Skeleton variant="rectangular" height={180} />
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={3} pb={3}>
          <MDTypography color="error">{error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!detailData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={3} pb={3}>
          <MDTypography>No hay datos para mostrar.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const totals = detailData.visitWithCollection || detailData.visit || {};

  return (
    <ErrorBoundary>
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={3} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                Detalle de recaudación
              </MDTypography>

              <MDBox sx={{ textAlign: "center" }}>
                <MDTypography variant="button" color="white" fontWeight="regular">
                  {`Id de la recaudación: ${visitId}`}
                </MDTypography>

                <MDTypography
                  variant="caption"
                  color="white"
                  sx={{ opacity: 0.85, display: "block" }}
                >
                  {detailData?.visit?.Date ? dateFmt.format(new Date(detailData.visit.Date)) : ""}
                </MDTypography>
              </MDBox>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button color="inherit" onClick={handleBackToRecaudaciones}>
                  {canViewLocalCollections
                    ? returnTo?.label || "Volver a recaudaciones"
                    : "Volver al local"}
                </Button>
              </Box>
            </MDBox>

            <MDBox mx={2} mt={3} px={2} pb={3}>
              {informativeMachinesError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No se pudieron cargar todas las máquinas informativas. La exportación A4 quedó deshabilitada.
                </Alert>
              )}
              <VisitTransactionDetails
                visit={detailData.visit}
                visitWithCollection={detailData.visitWithCollection || totals}
                local={canonicalDetailLocal || detailData.local || null}
                person={detailData.person || null}
                machines={mergedMachines}
                lastLocalVisitBeforeId={detailData?.LastLocalVisitBeforeId ?? null}
                lastLocalVisitBeforeDate={detailData?.LastLocalVisitBeforeDate ?? null}
                showPreviousLocalCollectionInfo
                collectionDetailPresentation
                a4DataReady={
                  !informativeMachinesLoading &&
                  !informativeMachinesError &&
                  informativeMachinesContextKey ===
                    `${Number(effectiveLocalId)}|${
                      detailData?.visit?.DateTime || detailData?.visit?.Date
                    }`
                }
              />

              <MDBox mt={4} display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={handlePrintTicket}
                  sx={secondaryOutlinedButtonSx}
                >
                  Imprimir ticket
                </Button>

                {effectiveLocalId && visitId && isLatestVisit && (
                  <EditVisitTransactionButton
                    visitId={String(visitId)}
                    local={canonicalDetailLocal || { Id: Number(effectiveLocalId) }}
                    displayDate={displayDate}
                    visitDateTime={detailData?.visit?.DateTime || null}
                    lastLocalVisitBeforeId={detailData?.LastLocalVisitBeforeId ?? null}
                    lastLocalVisitBeforeDate={detailData?.LastLocalVisitBeforeDate ?? null}
                    returnTo={returnTo}
                  />
                )}
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>
      </DashboardLayout>
    </ErrorBoundary>
  );
}
